import React, { useEffect, useMemo, useState } from "react";
import { scheduleDeferredDeleteWithUndo } from "@/lib/deferredDeleteWithUndo";
import { showActionError, showActionErrorFromUnknown, showActionSuccess } from "@/lib/feedback";
import {
  MOBILE_PHONE_REGEX,
  MOBILE_PHONE_VALIDATION_MESSAGE,
} from "@/modules/contacts/contactFormUtils";
import { isValidEmailForUniquenessCheck } from "@/modules/contacts/contactUniqueness";
import { useKeycloak } from "@/modules/auth/context/KeycloakContext";
import { Button } from "@/components/atoms/Button";
import { Input } from "@/components/atoms/Input";
import { MenuSelect } from "@/components/molecules/MenuSelect";
import { useCountries } from "@/hooks/useCountries";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import {
  Building2,
  GitBranch,
  ArrowRight,
  User,
  Plus,
  Save,
  Users,
  MessageCircle,
  ExternalLink,
  RefreshCw,
} from "lucide-react";
import { CreateLegalEntityDialog } from "./CreateLegalEntityDialog";
import { CreateBranchDialog } from "./CreateBranchDialog";
import { AddTeamMemberDialog } from "./AddTeamMemberDialog";
import { EditEntityDialog } from "./EditEntityDialog";
import { MoveEntityDialog } from "./MoveEntityDialog";
import { useProfileStore } from "@/store/profileStore";
import { useUiStore } from "@/store/uiStore";
import { useSettingsStore } from "@/store/settingsStore";
import { profileService, LegalEntityProfile, BranchProfile, LegalEntityHierarchy } from "@/services/profileService";
import { branchesService } from "@/services/branchesService";
import { legalEntitiesService } from "@/services/legalEntitiesService";
import { LogoUploadField } from "@/components/molecules/LogoUploadField";
import { normalizeLogoUrlForApi, resolveLogoDisplayUrl } from "@/lib/logoUpload";
import { Edit3, Trash2, ArrowRightLeft, CheckSquare, Square, Star } from "lucide-react";

const EMPTY_LE_FORM: Partial<LegalEntityProfile> = {
  primaryEmail: "",
  primaryPhone: "",
  website: "",
  registeredStreet: "",
  registeredTown: "",
  registeredCounty: "",
  registeredPostcode: "",
  registeredCountryCode: "",
  logoUrl: "",
};

const EMPTY_BRANCH_FORM: Partial<BranchProfile> = {
  name: "",
  email: "",
  workPhone: "",
  website: "",
  logoUrl: "",
  facebookUrl: "",
  instagramUrl: "",
  linkedinUrl: "",
};

const MAX_STREET_LENGTH = 255;
const MAX_TOWN_LENGTH = 100;
const MAX_COUNTY_LENGTH = 100;
const MAX_POSTCODE_LENGTH = 12;
const INPUT_VALIDATION_DEBOUNCE_MS = 400;

function isValidWebsite(value: string): boolean {
  try {
    const url = new URL(value.includes("://") ? value : `https://${value}`);
    return (
      (url.protocol === "http:" || url.protocol === "https:") &&
      url.hostname.includes(".")
    );
  } catch {
    return false;
  }
}

function maxLengthError(value: string, label: string, maxLength: number): string {
  return value.length > maxLength
    ? `${label} must be ${maxLength} characters or fewer`
    : "";
}

function findLeInHierarchy(entities: LegalEntityHierarchy[], id: string): LegalEntityHierarchy | undefined {
  for (const le of entities) {
    if (le.id === id) return le;
    const found = findLeInHierarchy(le.subEntities ?? [], id);
    if (found) return found;
  }
  return undefined;
}

function collectLegalEntityIds(
  entities: LegalEntityHierarchy[],
  ids: Set<string> = new Set<string>(),
): Set<string> {
  for (const entity of entities) {
    ids.add(entity.id);
    if (Array.isArray(entity.subEntities)) {
      collectLegalEntityIds(entity.subEntities, ids);
    }
  }
  return ids;
}

export const ProfileSetupPage: React.FC = () => {
  const { startPageLoading, stopPageLoading } = useUiStore();
  const { keycloak } = useKeycloak();
  const email = keycloak?.tokenParsed?.email as string | undefined;

  const { profileData: profile, refreshProfile, fetchProfile } = useProfileStore();
  const { legalEntityId } = useSettingsStore();
  const [legalEntity, setLegalEntity] = useState<LegalEntityProfile | null>(null);
  const [branch, setBranch] = useState<BranchProfile | null>(null);
  const [leForm, setLeForm] = useState<Partial<LegalEntityProfile>>(EMPTY_LE_FORM);
  const [branchForm, setBranchForm] = useState<Partial<BranchProfile>>(EMPTY_BRANCH_FORM);
  const [loading, setLoading] = useState(true);
  const { countryCodeOptions, loading: loadingCountries } = useCountries();

  useEffect(() => {
    if (loading) startPageLoading();
    else stopPageLoading();
    return () => stopPageLoading();
  }, [loading, startPageLoading, stopPageLoading]);

  const [savingLe, setSavingLe] = useState(false);
  const [savingBranch, setSavingBranch] = useState(false);
  const [leSaved, setLeSaved] = useState(false);
  const [branchSaved, setBranchSaved] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [syncingLeLogo, setSyncingLeLogo] = useState(false);
  const [syncingBranchLogo, setSyncingBranchLogo] = useState(false);

  const [createLeOpen, setCreateLeOpen] = useState(false);
  const [createBranchOpen, setCreateBranchOpen] = useState(false);
  const [addTeamMemberOpen, setAddTeamMemberOpen] = useState(false);
  const [selectedEntityId, setSelectedEntityId] = useState<string | null>(null);

  const [selectedLeIds, setSelectedLeIds] = useState<string[]>([]);
  const [editEntity, setEditEntity] = useState<{
    type: "LEGAL_ENTITY" | "BRANCH";
    id: string;
    name: string;
    email: string;
    phoneNumber: string;
    logoUrl?: string | null;
    whatsappEnabled?: boolean | null;
  } | null>(null);
  const [moveEntity, setMoveEntity] = useState<{ type: "LEGAL_ENTITY" | "BRANCH"; ids: string[] } | null>(null);
  const [hiddenHierarchyKeys, setHiddenHierarchyKeys] = useState<Set<string>>(() => new Set());

  const displayedEntities = profile?.userType === "ATLAS_MASTER_USER"
    ? profile.legalEntities.filter(le => le.id === legalEntityId || !legalEntityId)
    : profile?.legalEntities;

  const canManageTenant =
    profile?.userType === "BRANCH_MANAGER" ||
    profile?.userType === "SUPER_ADMIN" ||
    profile?.userType === "ATLAS_MASTER_USER";

  const dialogDefaultEntityId = useMemo(() => {
    if (!displayedEntities?.length) return undefined;

    const ids = collectLegalEntityIds(displayedEntities);
    if (selectedEntityId && ids.has(selectedEntityId)) return selectedEntityId;
    return displayedEntities[0]?.id;
  }, [displayedEntities, selectedEntityId]);

  const isWhatsAppEligible = !!(
    branchForm.workPhone?.trim() &&
    branchForm.website?.trim() &&
    branchForm.facebookUrl?.trim()
  );

  const currentLeHierarchy = useMemo(
    () => (profile && legalEntity ? findLeInHierarchy(profile.legalEntities, legalEntity.id) : undefined),
    [profile, legalEntity],
  );
  const currentBranchHierarchy = useMemo(
    () => currentLeHierarchy?.branches.find((b) => b.id === branch?.id),
    [currentLeHierarchy, branch],
  );
  const isLeWhatsAppConnected = Boolean(currentLeHierarchy?.whatsappEnabled);
  const isBranchWhatsAppConnected = Boolean(currentBranchHierarchy?.whatsappEnabled);

  // Legal Entity Details form: validation + dirty tracking for the Save button.
  const leEmail = (leForm.primaryEmail ?? "").trim();
  const debouncedLeEmail = useDebouncedValue(leEmail, INPUT_VALIDATION_DEBOUNCE_MS);
  const hasLeEmailError = Boolean(leEmail && !isValidEmailForUniquenessCheck(leEmail));
  const leEmailError =
    debouncedLeEmail && !isValidEmailForUniquenessCheck(debouncedLeEmail)
      ? "Enter a valid email address"
      : "";
  const lePhone = (leForm.primaryPhone ?? "").trim();
  const debouncedLePhone = useDebouncedValue(lePhone, INPUT_VALIDATION_DEBOUNCE_MS);
  const hasLePhoneError = Boolean(lePhone && !MOBILE_PHONE_REGEX.test(lePhone));
  const lePhoneError =
    debouncedLePhone && !MOBILE_PHONE_REGEX.test(debouncedLePhone)
      ? MOBILE_PHONE_VALIDATION_MESSAGE
      : "";
  const leWebsite = (leForm.website ?? "").trim();
  const debouncedLeWebsite = useDebouncedValue(leWebsite, INPUT_VALIDATION_DEBOUNCE_MS);
  const hasLeWebsiteError = Boolean(leWebsite && !isValidWebsite(leWebsite));
  const leWebsiteError =
    debouncedLeWebsite && !isValidWebsite(debouncedLeWebsite)
      ? "Enter a valid website URL"
      : "";
  const leCountryCode = (leForm.registeredCountryCode ?? "").trim();
  const leCountryCodeError =
    leCountryCode && !/^[A-Za-z]{2}$/.test(leCountryCode)
      ? "Select a valid two-letter country code"
      : "";
  const lePostcode = (leForm.registeredPostcode ?? "").trim();
  const lePostcodeError = maxLengthError(lePostcode, "Postcode", MAX_POSTCODE_LENGTH);
  const leStreetError = maxLengthError(
    (leForm.registeredStreet ?? "").trim(),
    "Street",
    MAX_STREET_LENGTH,
  );
  const leTownError = maxLengthError(
    (leForm.registeredTown ?? "").trim(),
    "Town",
    MAX_TOWN_LENGTH,
  );
  const leCountyError = maxLengthError(
    (leForm.registeredCounty ?? "").trim(),
    "County",
    MAX_COUNTY_LENGTH,
  );

  const isLeDirty = useMemo(() => {
    if (!legalEntity) return false;
    return (
      (leForm.primaryPhone ?? "") !== (legalEntity.primaryPhone ?? "") ||
      (leForm.primaryEmail ?? "") !== (legalEntity.primaryEmail ?? "") ||
      (leForm.website ?? "") !== (legalEntity.website ?? "") ||
      (leForm.registeredStreet ?? "") !== (legalEntity.registeredStreet ?? "") ||
      (leForm.registeredTown ?? "") !== (legalEntity.registeredTown ?? "") ||
      (leForm.registeredCounty ?? "") !== (legalEntity.registeredCounty ?? "") ||
      (leForm.registeredPostcode ?? "") !== (legalEntity.registeredPostcode ?? "") ||
      (leForm.registeredCountryCode ?? "") !== (legalEntity.registeredCountryCode ?? "")
    );
  }, [leForm, legalEntity]);

  const canSaveLe =
    isLeDirty &&
    !hasLeEmailError &&
    !hasLePhoneError &&
    !hasLeWebsiteError &&
    !leCountryCodeError &&
    !lePostcodeError &&
    !leStreetError &&
    !leTownError &&
    !leCountyError;

  const handleConnectWhatsApp = () => {
    if (!branch?.id) return;
    const META_APP_ID = import.meta.env.VITE_META_APP_ID;
    const CONFIG_ID = import.meta.env.VITE_META_CONFIG_ID;
    const REDIRECT_URI = encodeURIComponent(window.location.origin + "/messages/verifycode");
    
    const stateObj = { entityId: branch.id, entityType: 'BR' };
    const stateParam = encodeURIComponent(btoa(JSON.stringify(stateObj)));

    const metaUrl = `https://www.facebook.com/v26.0/dialog/oauth?client_id=${META_APP_ID}&redirect_uri=${REDIRECT_URI}&response_type=code&config_id=${CONFIG_ID}&state=${stateParam}`;
    window.location.href = metaUrl;
  };

  const handleSyncLogoResult = (result: { status: string; warning: string | null }) => {
    if (result.status === "SYNCED") {
      showActionSuccess("Logo synced to WhatsApp");
    } else if (result.status === "NO_LOGO") {
      showActionError("Upload a logo above before syncing.", { title: "Nothing to sync" });
    } else if (result.status === "NO_CHANNEL") {
      showActionError("Connect WhatsApp before syncing a logo.", { title: "Not connected" });
    } else {
      showActionError(result.warning ?? "Failed to sync logo to WhatsApp");
    }
  };

  const handleSyncLeLogo = async () => {
    if (!email) return;
    setSyncingLeLogo(true);
    try {
      const result = await profileService.syncLegalEntityLogo(email);
      handleSyncLogoResult(result);
    } catch (err) {
      showActionErrorFromUnknown(err, "Failed to sync logo to WhatsApp");
    } finally {
      setSyncingLeLogo(false);
    }
  };

  const handleSyncBranchLogo = async () => {
    if (!email) return;
    setSyncingBranchLogo(true);
    try {
      const result = await profileService.syncBranchLogo(email);
      handleSyncLogoResult(result);
    } catch (err) {
      showActionErrorFromUnknown(err, "Failed to sync logo to WhatsApp");
    } finally {
      setSyncingBranchLogo(false);
    }
  };

  const refresh = async () => {
    if (!email) return;
    try {
      const [le, br] = await Promise.all([
        profileService.getLegalEntity(email).catch(() => null),
        profileService.getBranch(email).catch(() => null),
      ]);
      await refreshProfile(email);
      if (le) {
        setLegalEntity(le);
        setLeForm({
          primaryEmail: le.primaryEmail ?? "",
          primaryPhone: le.primaryPhone ?? "",
          website: le.website ?? "",
          registeredStreet: le.registeredStreet ?? "",
          registeredTown: le.registeredTown ?? "",
          registeredCounty: le.registeredCounty ?? "",
          registeredPostcode: le.registeredPostcode ?? "",
          registeredCountryCode: le.registeredCountryCode ?? "",
          logoUrl: normalizeLogoUrlForApi(le.logoUrl),
        });
      }
      if (br) {
        setBranch(br);
        setBranchForm({
          name: br.name ?? "",
          email: br.email ?? "",
          workPhone: br.workPhone ?? "",
          website: br.website ?? "",
          logoUrl: normalizeLogoUrlForApi(br.logoUrl),
          facebookUrl: br.facebookUrl ?? "",
          instagramUrl: br.instagramUrl ?? "",
          linkedinUrl: br.linkedinUrl ?? "",
        });
      }
      if (profile && profile.legalEntities.length > 0 && !selectedEntityId) {
        setSelectedEntityId(profile.legalEntities[0].id);
      }
    } catch (err: any) {
      showActionErrorFromUnknown(err, "Failed to load profile");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (email) fetchProfile(email);
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, fetchProfile]);

  const handleSaveLe = async () => {
    if (!email || !canSaveLe) return;
    setSavingLe(true);
    setLeSaved(false);
    try {
      const { logoUrl: _logoUrl, ...leUpdates } = leForm;
      const updated = await profileService.updateLegalEntity(email, leUpdates);
      setLegalEntity(updated);
      setLeSaved(true);
      setTimeout(() => setLeSaved(false), 2000);
    } catch (err: any) {
      showActionErrorFromUnknown(err, "Failed to save legal entity details");
    } finally {
      setSavingLe(false);
    }
  };

  const handleSaveBranch = async () => {
    if (!email) return;
    setSavingBranch(true);
    setBranchSaved(false);
    try {
      const { logoUrl: _logoUrl, ...branchUpdates } = branchForm;
      const updated = await profileService.updateBranch(email, branchUpdates);
      setBranch(updated);
      setBranchSaved(true);
      setTimeout(() => setBranchSaved(false), 2000);
    } catch (err: any) {
      showActionErrorFromUnknown(err, "Failed to save branch details");
    } finally {
      setSavingBranch(false);
    }
  };

  const agencyPreviewName =
    profile?.companyName?.trim() ||
    legalEntity?.displayName?.trim() ||
    legalEntity?.tradingName?.trim() ||
    null;

  const handleAgencyLogoChange = async (logoUrl: string | null) => {
    if (!email || !legalEntity?.id) return;
    const normalized = normalizeLogoUrlForApi(logoUrl);
    setLeForm((prev) => ({ ...prev, logoUrl: normalized }));
    try {
      await legalEntitiesService.update(legalEntity.id, { logoUrl: normalized });
      const updated = await profileService
        .updateLegalEntity(email, { logoUrl: normalized })
        .catch(() => undefined);
      if (updated?.metaLogoSyncWarning) {
        showActionError(updated.metaLogoSyncWarning, { title: "Logo saved" });
      }
      setLegalEntity((prev) => (prev ? { ...prev, logoUrl: normalized } : prev));
      await refreshProfile(email);
    } catch (err: unknown) {
      showActionErrorFromUnknown(err, "Failed to save agency logo");
      void refresh();
    }
  };

  const handleBranchLogoChange = async (logoUrl: string | null) => {
    if (!email || !branch?.id) return;
    const normalized = normalizeLogoUrlForApi(logoUrl);
    setBranchForm((prev) => ({ ...prev, logoUrl: normalized }));
    try {
      const updated = await profileService.updateBranch(email, { logoUrl: normalized });
      if (updated.metaLogoSyncWarning) {
        showActionError(updated.metaLogoSyncWarning, { title: "Logo saved" });
      }
      await branchesService.update(branch.id, { logoUrl: normalized }).catch(() => undefined);
      setBranch((prev) => (prev ? { ...prev, logoUrl: normalized } : prev));
      await refreshProfile(email);
    } catch (err: unknown) {
      showActionErrorFromUnknown(err, "Failed to save branch logo");
      void refresh();
    }
  };

  const handleComplete = async () => {
    if (!email) return;
    setCompleting(true);
    try {
      await profileService.complete(email);
      window.location.replace("/");
    } catch (err: any) {
      showActionErrorFromUnknown(err, "Failed to complete setup");
      setCompleting(false);
    }
  };

  const handleDelete = async (type: "LEGAL_ENTITY" | "BRANCH", id: string, name: string) => {
    if (!email) return;

    const hideKey = `${type}:${id}`;
    const kind = type === "BRANCH" ? "branch" : "legal entity";

    const showAgain = () => {
      setHiddenHierarchyKeys((prev) => {
        const next = new Set(prev);
        next.delete(hideKey);
        return next;
      });
    };

    scheduleDeferredDeleteWithUndo({
      idKey: `profile:${type}:${id}`,
      label: name,
      hint: `This ${kind} will also be removed from Keycloak.`,
      onScheduled: () => setHiddenHierarchyKeys((prev) => new Set(prev).add(hideKey)),
      onUndo: showAgain,
      onFailed: showAgain,
      onCommit: () => profileService.deleteEntity(email, type, id),
      onComplete: () => {
        // Keep the row hidden until fresh profile data arrives — un-hiding first
        // flashes the branch from stale cache for a moment after the countdown.
        void refresh().then(() => showAgain());
      },
    });
  };

  const handleSetPrimary = async (branchId: string) => {
    if (!email) return;
    try {
      await branchesService.setPrimary(branchId);
      profileService.invalidate(email);
      void refresh();
    } catch (err: any) {
      showActionErrorFromUnknown(err, "Failed to set primary branch");
    }
  };

  const toggleLeSelection = (id: string) => {
    setSelectedLeIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-bridge-accent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-full py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto space-y-10">
        <div className="text-center">
          <h1 className="text-4xl font-extrabold text-slate-900 tracking-tight">
            {profile?.profileCompleted ? "Business Profile" : "Complete Your Profile"}
          </h1>
          <p className="mt-3 text-lg text-slate-600">
            {profile?.profileCompleted 
              ? "Manage your company details and business hierarchy." 
              : "Fill in your company details, then optionally add more legal entities or branches."}
          </p>
        </div>

        {/* WhatsApp Banner */}
        {isWhatsAppEligible && (
          <div className="bg-bridge-accent/5 border border-bridge-accent/20 rounded-3xl p-6 shadow-sm flex flex-col md:flex-row items-center justify-between gap-6 animate-in slide-in-from-top duration-500">
            <div className="flex items-center gap-5 text-slate-900">
              <div className="w-14 h-14 rounded-2xl bg-[#009BF5] text-white flex items-center justify-center shadow-lg shadow-[#009BF5]/25 shrink-0">
                <MessageCircle className="w-7 h-7" />
              </div>
              <div>
                <h3 className="text-xl font-bold">Bring your business to life on WhatsApp</h3>
                <p className="text-slate-600">Link your WhatsApp Business account now to start communicating with customers.</p>
              </div>
            </div>
            <Button 
              onClick={handleConnectWhatsApp}
              className="bg-[#009BF5] hover:bg-[#0085d4] text-white px-8 py-6 rounded-2xl font-bold shadow-xl shadow-[#009BF5]/25 transition-all hover:scale-105 active:scale-95 whitespace-nowrap"
            >
              <ExternalLink className="w-5 h-5 mr-2" />
              Connect Now!
            </Button>
          </div>
        )}

        {/* Basic Info */}
        <div className="glass rounded-3xl p-8 shadow-premium transition-all duration-300 hover:shadow-premium-hover">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-12 rounded-2xl bg-bridge-accent/10 text-bridge-accent flex items-center justify-center">
              <User className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-slate-900">Basic Information</h2>
              <p className="text-slate-500">Your account identity and company association</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {legalEntity ? (
              <div className="md:col-span-2">
                <LogoUploadField
                  label="Agency Logo"
                  logoUrl={leForm.logoUrl}
                  displayLogoUrl={legalEntity.logoUrl}
                  previewName={agencyPreviewName}
                  onChange={handleAgencyLogoChange}
                                  />
                {isLeWhatsAppConnected && (
                  <div className="mt-2 flex justify-end">
                    <Button variant="outline" size="sm" onClick={handleSyncLeLogo} disabled={syncingLeLogo}>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      {syncingLeLogo ? "Syncing..." : "Sync logo to WhatsApp"}
                    </Button>
                  </div>
                )}
              </div>
            ) : null}
            <Labeled label="Email Address">
              <Input value={profile?.email || ""} disabled className="bg-slate-50 border-slate-200" />
            </Labeled>
            <Labeled label="Company">
              <Input value={profile?.companyName || ""} disabled className="bg-slate-50 border-slate-200" />
            </Labeled>
          </div>
        </div>

        {/* Legal Entity Details (saved on the legal_entities row, not the user) */}
        {legalEntity && (
          <Card accent="bg-[#009BF5]" icon={<Building2 className="w-5 h-5" />} iconBg="bg-bridge-accent/10 text-[#009BF5]" title={`${legalEntity.displayName ?? "Your Company"} — Details`}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Labeled label="Phone">
                <Input
                  value={leForm.primaryPhone ?? ""}
                  onChange={(e) => setLeForm({ ...leForm, primaryPhone: e.target.value.replace(/[^\d+]/g, "") })}
                  placeholder="Enter company phone number"
                  inputMode="tel"
                  aria-invalid={Boolean(lePhoneError)}
                />
                {lePhoneError && <p className="text-xs text-red-500 font-medium mt-1">{lePhoneError}</p>}
              </Labeled>
              <Labeled label="Email">
                <Input
                  type="email"
                  value={leForm.primaryEmail ?? ""}
                  onChange={(e) => setLeForm({ ...leForm, primaryEmail: e.target.value })}
                  placeholder="Enter company email address"
                  aria-invalid={Boolean(leEmailError)}
                />
                {leEmailError && <p className="text-xs text-red-500 font-medium mt-1">{leEmailError}</p>}
              </Labeled>
              <Labeled label="Website">
                <Input
                  value={leForm.website ?? ""}
                  onChange={(e) => setLeForm({ ...leForm, website: e.target.value })}
                  placeholder="Enter company website URL"
                  aria-invalid={Boolean(leWebsiteError)}
                />
                {leWebsiteError && <p className="text-xs text-red-500 font-medium mt-1">{leWebsiteError}</p>}
              </Labeled>
              <Labeled label="Street">
                <Input
                  value={leForm.registeredStreet ?? ""}
                  onChange={(e) => setLeForm({ ...leForm, registeredStreet: e.target.value })}
                  placeholder="Enter registered street address"
                  aria-invalid={Boolean(leStreetError)}
                />
                {leStreetError && <p className="text-xs text-red-500 font-medium mt-1">{leStreetError}</p>}
              </Labeled>
              <Labeled label="Town">
                <Input
                  value={leForm.registeredTown ?? ""}
                  onChange={(e) => setLeForm({ ...leForm, registeredTown: e.target.value })}
                  placeholder="Enter town or city"
                  aria-invalid={Boolean(leTownError)}
                />
                {leTownError && <p className="text-xs text-red-500 font-medium mt-1">{leTownError}</p>}
              </Labeled>
              <Labeled label="County">
                <Input
                  value={leForm.registeredCounty ?? ""}
                  onChange={(e) => setLeForm({ ...leForm, registeredCounty: e.target.value })}
                  placeholder="Enter county or region"
                  aria-invalid={Boolean(leCountyError)}
                />
                {leCountyError && <p className="text-xs text-red-500 font-medium mt-1">{leCountyError}</p>}
              </Labeled>
              <Labeled label="Postcode">
                <Input
                  value={leForm.registeredPostcode ?? ""}
                  onChange={(e) => setLeForm({ ...leForm, registeredPostcode: e.target.value })}
                  placeholder="Enter postcode"
                  aria-invalid={Boolean(lePostcodeError)}
                />
                {lePostcodeError && (
                  <p className="text-xs text-red-500 font-medium mt-1">{lePostcodeError}</p>
                )}
              </Labeled>
              <Labeled label="Country">
                <MenuSelect
                  value={leForm.registeredCountryCode ?? ""}
                  onChange={(registeredCountryCode) =>
                    setLeForm((prev) => ({ ...prev, registeredCountryCode }))
                  }
                  options={countryCodeOptions}
                  placeholder="Select country"
                  searchable
                  loading={loadingCountries}
                />
                {leCountryCodeError && (
                  <p className="text-xs text-red-500 font-medium mt-1">{leCountryCodeError}</p>
                )}
              </Labeled>
            </div>
            <div className="mt-6 flex justify-end">
              <Button onClick={handleSaveLe} disabled={savingLe || !canSaveLe} className="bg-[#009BF5] hover:bg-[#0085d4] text-white">
                <Save className="w-4 h-4 mr-2" />
                {savingLe ? "Saving..." : leSaved ? "Saved!" : "Save Details"}
              </Button>
            </div>
          </Card>
        )}

        {/* Branch Details */}
        {branch && (
          <Card accent="bg-[#009BF5]" icon={<GitBranch className="w-5 h-5" />} iconBg="bg-bridge-accent/10 text-[#009BF5]" title={`${branch.name ?? "Your Branch"} — Details`}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Labeled label="Branch Name">
                <Input value={branchForm.name ?? ""} onChange={(e) => setBranchForm({ ...branchForm, name: e.target.value })} placeholder="Enter branch name" />
              </Labeled>
              <Labeled label="Phone">
                <Input value={branchForm.workPhone ?? ""} onChange={(e) => setBranchForm({ ...branchForm, workPhone: e.target.value })} placeholder="Enter branch phone number" />
              </Labeled>
              <Labeled label="Email">
                <Input value={branchForm.email ?? ""} onChange={(e) => setBranchForm({ ...branchForm, email: e.target.value })} placeholder="Enter branch email address" />
              </Labeled>
              <Labeled label="Website">
                <Input value={branchForm.website ?? ""} onChange={(e) => setBranchForm({ ...branchForm, website: e.target.value })} placeholder="Enter branch website URL" />
              </Labeled>
              <div className="md:col-span-2">
                <LogoUploadField
                  label="Branch Logo"
                  logoUrl={branchForm.logoUrl}
                  displayLogoUrl={branch.logoUrl}
                  previewName={branchForm.name ?? branch.name}
                  onChange={handleBranchLogoChange}
                                  />
                {isBranchWhatsAppConnected && (
                  <div className="mt-2 flex justify-end">
                    <Button variant="outline" size="sm" onClick={handleSyncBranchLogo} disabled={syncingBranchLogo}>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      {syncingBranchLogo ? "Syncing..." : "Sync logo to WhatsApp"}
                    </Button>
                  </div>
                )}
              </div>
              <Labeled label="Facebook">
                <Input value={branchForm.facebookUrl ?? ""} onChange={(e) => setBranchForm({ ...branchForm, facebookUrl: e.target.value })} placeholder="Enter Facebook page URL" />
              </Labeled>
              <Labeled label="Instagram">
                <Input value={branchForm.instagramUrl ?? ""} onChange={(e) => setBranchForm({ ...branchForm, instagramUrl: e.target.value })} placeholder="Enter Instagram profile URL" />
              </Labeled>
              <Labeled label="LinkedIn">
                <Input value={branchForm.linkedinUrl ?? ""} onChange={(e) => setBranchForm({ ...branchForm, linkedinUrl: e.target.value })} placeholder="Enter LinkedIn company URL" />
              </Labeled>
            </div>
            <div className="mt-6 flex justify-end">
              <Button onClick={handleSaveBranch} disabled={savingBranch} className="bg-[#009BF5] hover:bg-[#0085d4] text-white">
                <Save className="w-4 h-4 mr-2" />
                {savingBranch ? "Saving..." : branchSaved ? "Saved!" : "Save Branch Details"}
              </Button>
            </div>
          </Card>
        )}

        {/* Team Members — Branch Managers can invite Support Users / other Branch Managers */}
        <Card accent="bg-[#009BF5]" icon={<Users className="w-5 h-5" />} iconBg="bg-bridge-accent/10 text-[#009BF5]" title="Your Team">
          <div className="space-y-4">
            <p className="text-sm text-slate-600 leading-relaxed">
              Invite people into a Legal Entity (and optionally a specific Branch). Pick from{" "}
              <strong>Support User</strong> or <strong>Branch Manager</strong>; they can log in
              straight away with the password you set.
            </p>
            <div className="flex justify-start">
              <Button
                onClick={() => setAddTeamMemberOpen(true)}
                disabled={!profile || profile.legalEntities.length === 0}
                className="bg-[#009BF5] hover:bg-[#0085d4] text-white border-transparent"
              >
                <Plus className="w-4 h-4 mr-2" /> Add Team Member
              </Button>
            </div>
          </div>
        </Card>

        {/* Hierarchy + actions */}
        <Card accent="bg-[#009BF5]" icon={<Building2 className="w-5 h-5" />} iconBg="bg-bridge-accent/10 text-[#009BF5]" title="Your Hierarchy">
          <div className="space-y-6">
            <div className="space-y-3">
              {displayedEntities?.map((le) => (
                <LegalEntityRow 
                  key={le.id} 
                  le={le} 
                  selectedId={selectedEntityId} 
                  onSelect={setSelectedEntityId} 
                  selectedLeIds={selectedLeIds}
                  onToggleSelection={toggleLeSelection}
                  hiddenHierarchyKeys={hiddenHierarchyKeys}
                  onEdit={(type, id, name, email, phoneNumber, logoUrl, whatsappEnabled) =>
                    setEditEntity({ type, id, name, email, phoneNumber, logoUrl, whatsappEnabled })
                  }
                  onDelete={handleDelete}
                  onMove={(type, ids) => setMoveEntity({ type, ids })}
                  onSetPrimary={handleSetPrimary}
                />
              ))}
            </div>

            <div className="flex flex-wrap gap-3">
              {canManageTenant && (
                <Button onClick={() => setCreateLeOpen(true)} className="bg-[#009BF5] hover:bg-[#0085d4] text-white">
                  <Plus className="w-4 h-4 mr-2" /> Create Legal Entity
                </Button>
              )}
              {canManageTenant && (
                <Button
                  onClick={() => setCreateBranchOpen(true)}
                  disabled={!profile || profile.legalEntities.length === 0}
                  className="bg-[#009BF5] hover:bg-[#0085d4] text-white"
                >
                  <Plus className="w-4 h-4 mr-2" /> Create Branch
                </Button>
              )}
              {selectedLeIds.length > 0 && (
                <Button
                  onClick={() => setMoveEntity({ type: "LEGAL_ENTITY", ids: selectedLeIds })}
                  className="bg-[#009BF5] hover:bg-[#0085d4] text-white"
                >
                  <ArrowRightLeft className="w-4 h-4 mr-2" /> Move Selected ({selectedLeIds.length})
                </Button>
              )}
            </div>
          </div>
        </Card>

        <div className="flex justify-end pt-4">
          <Button
            onClick={handleComplete}
            disabled={completing}
            size="lg"
            className="px-8 py-6 text-lg font-bold bg-[#009BF5] hover:bg-[#0085d4] text-white rounded-2xl shadow-xl shadow-[#009BF5]/25"
          >
            {completing ? "Finishing..." : profile?.profileCompleted ? "Go to Dashboard" : "Complete Setup"} <ArrowRight className="w-6 h-6 ml-3" />
          </Button>
        </div>
      </div>

      {email && (
        <>
          <CreateLegalEntityDialog
            callerEmail={email}
            legalEntities={displayedEntities ?? []}
            defaultParentId={dialogDefaultEntityId}
            open={createLeOpen}
            onClose={() => setCreateLeOpen(false)}
            onCreated={() => void refresh()}
          />
          <CreateBranchDialog
            callerEmail={email}
            legalEntities={displayedEntities ?? []}
            defaultLegalEntityId={dialogDefaultEntityId}
            open={createBranchOpen}
            onClose={() => setCreateBranchOpen(false)}
            onCreated={() => void refresh()}
          />
          <AddTeamMemberDialog
            callerEmail={email}
            legalEntities={displayedEntities ?? []}
            defaultLegalEntityId={dialogDefaultEntityId}
            open={addTeamMemberOpen}
            onClose={() => setAddTeamMemberOpen(false)}
            onCreated={() => void refresh()}
          />
          {editEntity && (
            <EditEntityDialog
              callerEmail={email}
              type={editEntity.type}
              id={editEntity.id}
              initialName={editEntity.name}
              initialEmail={editEntity.email}
              emailLocked={editEntity.type === "LEGAL_ENTITY" && (displayedEntities ?? []).some((le) => le.id === editEntity.id)}
              initialPhoneNumber={editEntity.phoneNumber}
              orgLegalEntityId={legalEntityId ?? editEntity.id}
              initialLogoUrl={editEntity.logoUrl}
              whatsappEnabled={editEntity.whatsappEnabled}
              open={!!editEntity}
              onClose={() => setEditEntity(null)}
              onUpdated={refresh}
            />
          )}
          {moveEntity && (
            <MoveEntityDialog
              callerEmail={email}
              type={moveEntity.type}
              selectedIds={moveEntity.ids}
              legalEntities={displayedEntities ?? []}
              open={!!moveEntity}
              onClose={() => setMoveEntity(null)}
              onMoved={() => {
                void refresh();
                setSelectedLeIds([]);
              }}
            />
          )}
        </>
      )}
    </div>
  );
};

const LegalEntityRow: React.FC<{
  le: LegalEntityHierarchy;
  selectedId: string | null;
  onSelect: (id: string) => void;
  depth?: number;
  selectedLeIds: string[];
  onToggleSelection: (id: string) => void;
  onEdit: (
    type: "LEGAL_ENTITY" | "BRANCH",
    id: string,
    name: string,
    email: string,
    phoneNumber: string,
    logoUrl?: string | null,
    whatsappEnabled?: boolean | null,
  ) => void;
  onDelete: (type: "LEGAL_ENTITY" | "BRANCH", id: string, name: string) => void;
  onMove: (type: "LEGAL_ENTITY" | "BRANCH", ids: string[]) => void;
  onSetPrimary: (branchId: string) => void;
  hiddenHierarchyKeys: Set<string>;
}> = ({
  le,
  selectedId,
  onSelect,
  depth = 0,
  selectedLeIds,
  onToggleSelection,
  onEdit,
  onDelete,
  onMove,
  onSetPrimary,
  hiddenHierarchyKeys,
}) => {
  if (hiddenHierarchyKeys.has(`LEGAL_ENTITY:${le.id}`)) return null;

  const visibleBranches = le.branches.filter((b) => !hiddenHierarchyKeys.has(`BRANCH:${b.id}`));
  const leContactEmail =
    (typeof le.primaryEmail === "string" && le.primaryEmail.trim()) ||
    (typeof (le as { email?: string | null }).email === "string" && (le as { email?: string | null }).email?.trim()) ||
    "";
  const lePhoneNumber = (typeof le.phoneNumber === "string" && le.phoneNumber.trim()) || "";

  return (
  <div className={cn("space-y-2", depth > 0 && "mt-3 ml-6 pl-4 border-l-2 border-bridge-accent/20")}>
    <div className="border border-slate-200 rounded-xl overflow-hidden group/row">
      <div
        className={`w-full flex items-center justify-between px-4 py-3 transition-colors ${
          selectedId === le.id ? "bg-bridge-accent/5" : "bg-white hover:bg-slate-50"
        }`}
      >
        <div className="flex items-center gap-4">
          <button 
            type="button" 
            onClick={(e) => { e.stopPropagation(); onToggleSelection(le.id); }}
            className="p-1 -ml-1 text-slate-400 hover:text-[#009BF5] transition-colors"
          >
            {selectedLeIds.includes(le.id) ? <CheckSquare className="w-5 h-5 text-[#009BF5]" /> : <Square className="w-5 h-5" />}
          </button>
          <button
            type="button"
            onClick={() => onSelect(le.id)}
            className="flex items-center gap-3 text-left py-1"
          >
            <Building2 className={cn("w-5 h-5 text-[#009BF5]", depth > 0 && "opacity-80")} />
            {resolveLogoDisplayUrl(le.logoUrl) && (
              <img
                src={resolveLogoDisplayUrl(le.logoUrl)!}
                alt="Profile logo"
                className="w-8 h-8 rounded-lg object-cover border border-slate-200 shrink-0"
              />
             )}
            <div className="flex flex-col">
              <span className={cn("font-bold text-slate-800 leading-tight", depth > 0 ? "text-sm" : "text-base")}>{le.name}</span>
              {leContactEmail ? <span className="text-[10px] text-slate-400 font-medium">{leContactEmail}</span> : null}
            </div>
          </button>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 opacity-60 group-hover/row:opacity-100 transition-all">
            <button 
              onClick={(e) => {
                e.stopPropagation();
                onEdit("LEGAL_ENTITY", le.id, le.name ?? "", leContactEmail, lePhoneNumber, le.logoUrl, le.whatsappEnabled);
              }} 
              className="p-2 rounded-xl bg-white hover:bg-bridge-accent/10 text-slate-500 hover:text-[#009BF5] shadow-sm border border-slate-200 transition-all"
              title="Edit"
            >
              <Edit3 className="w-4 h-4" />
            </button>
            <button 
              onClick={(e) => { e.stopPropagation(); onMove("LEGAL_ENTITY", [le.id]); }} 
              className="p-2 rounded-xl bg-white hover:bg-bridge-accent/10 text-slate-500 hover:text-[#009BF5] shadow-sm border border-slate-200 transition-all"
              title="Move"
            >
              <ArrowRightLeft className="w-4 h-4" />
            </button>
            <button 
              onClick={(e) => { e.stopPropagation(); onDelete("LEGAL_ENTITY", le.id, le.name); }} 
              className="p-2 rounded-xl bg-white hover:bg-red-50 text-slate-500 hover:text-red-600 shadow-sm border border-slate-200 transition-all"
              title="Delete"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
          {visibleBranches.length > 1 && !visibleBranches.some((b) => b.isPrimary) && (
            <span className="hidden sm:inline-flex items-center gap-1 text-xs font-semibold text-amber-600 bg-amber-50 px-2.5 py-1 rounded-full border border-amber-200" title="Select a primary branch for this legal entity">
              <Star className="w-3.5 h-3.5" /> Choose primary
            </span>
          )}
          <span className="hidden sm:inline-block text-xs font-medium text-slate-500 bg-slate-100 px-2.5 py-1 rounded-full border border-slate-200">
            {visibleBranches.length} branch{visibleBranches.length === 1 ? "" : "es"}
          </span>
        </div>
      </div>
      {visibleBranches.length > 0 && (
        <ul className="bg-slate-50/50 border-t border-slate-200 divide-y divide-slate-200/60">
          {visibleBranches.map((b) => (
            <li key={b.id} className="flex items-center justify-between px-5 py-3 text-sm group/branch">
              <div className="flex items-center gap-3 text-slate-700 font-medium">
                <GitBranch className="w-4 h-4 text-[#009BF5]" />
                {resolveLogoDisplayUrl(b.logoUrl) && (
                  <img
                    src={resolveLogoDisplayUrl(b.logoUrl)!}
                    alt="Branch logo"
                    className="w-7 h-7 rounded-md object-cover border border-slate-200 shrink-0"
                  />
                )}
                <div className="flex flex-col">
                  <span>{b.name}</span>
                  {b.email && <span className="text-[10px] text-slate-400 font-normal leading-none">{b.email}</span>}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 opacity-0 group-hover/branch:opacity-100 transition-all">
                  <button 
                    onClick={() =>
                      onEdit("BRANCH", b.id, b.name ?? "", b.email || "", b.phoneNumber || "", b.logoUrl, b.whatsappEnabled)
                    }
                    className="p-1.5 rounded-lg bg-white hover:bg-bridge-accent/10 text-slate-500 hover:text-[#009BF5] border border-slate-200 shadow-sm transition-all"
                    title="Edit"
                  >
                    <Edit3 className="w-3.5 h-3.5" />
                  </button>
                  <button 
                    onClick={() => onMove("BRANCH", [b.id])} 
                    className="p-1.5 rounded-lg bg-white hover:bg-bridge-accent/10 text-slate-500 hover:text-[#009BF5] border border-slate-200 shadow-sm transition-all"
                    title="Move"
                  >
                    <ArrowRightLeft className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => onDelete("BRANCH", b.id, b.name)}
                    className="p-1.5 rounded-lg bg-white hover:bg-red-50 text-slate-500 hover:text-red-600 border border-slate-200 shadow-sm transition-all"
                    title="Delete"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => { if (!b.isPrimary) onSetPrimary(b.id); }}
                  disabled={!!b.isPrimary}
                  className={cn(
                    "flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border transition-all",
                    b.isPrimary
                      ? "bg-amber-50 text-amber-600 border-amber-200 cursor-default"
                      : "bg-white text-slate-400 border-slate-200 hover:text-amber-500 hover:border-amber-200"
                  )}
                  title={b.isPrimary ? "Primary branch for this legal entity" : "Set as primary branch"}
                >
                  <Star className={cn("w-3.5 h-3.5", b.isPrimary && "fill-amber-400 text-amber-500")} />
                  {b.isPrimary ? "Primary" : "Set primary"}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
    
    {le.subEntities && le.subEntities.length > 0 && (
      <div className="space-y-3">
        {le.subEntities.map((sub) => (
          <LegalEntityRow 
            key={sub.id} 
            le={sub} 
            selectedId={selectedId} 
            onSelect={onSelect} 
            depth={depth + 1} 
            selectedLeIds={selectedLeIds}
            onToggleSelection={onToggleSelection}
            onEdit={onEdit}
            onDelete={onDelete}
            onMove={onMove}
            onSetPrimary={onSetPrimary}
            hiddenHierarchyKeys={hiddenHierarchyKeys}
          />
        ))}
      </div>
    )}
  </div>
  );
};

const Card: React.FC<{
  accent: string;
  icon: React.ReactNode;
  iconBg: string;
  title: string;
  children: React.ReactNode;
}> = ({ accent, icon, iconBg, title, children }) => (
  <div className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 overflow-hidden relative">
    <div className={`absolute top-0 left-0 w-1 h-full ${accent}`} />
    <div className="p-6 border-b border-slate-100">
      <h2 className="text-xl font-bold flex items-center gap-3 text-slate-800">
        <div className={`p-2 rounded-lg ${iconBg}`}>{icon}</div>
        {title}
      </h2>
    </div>
    <div className="p-8">{children}</div>
  </div>
);

const Labeled: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="space-y-2">
    <label className="text-sm font-semibold text-slate-700">{label}</label>
    {children}
  </div>
);

function cn(...inputs: any[]) {
  return inputs.filter(Boolean).join(" ");
}
